import {
  RUTERO_DECLARABLE_PASSENGER_FIELDS,
  RUTERO_HOLD_PASSENGER_MAX,
} from '@bandido/shared/types';
import type { RuteroDeclarablePassengerField } from '@bandido/shared/types';

import type { CommittedPassenger } from './committedBookingContext';
import { compositionRequiresBirthDate } from './passengerFields';
import type { DeclaredPassengerField } from './passengerFields';
import type { RuteroPassengerPayload } from './queries';

const EMPTY_PASSENGER: RuteroPassengerPayload = {
  type: 'adult',
  firstName: '',
  lastName: '',
};

export function bookingPassengersFromParty(
  party: readonly CommittedPassenger[] | undefined,
): RuteroPassengerPayload[] {
  const rows = party === undefined ? [{ type: 'adult' as const }] : party;
  return rows.slice(0, RUTERO_HOLD_PASSENGER_MAX).map((passenger, index) => ({
    ...EMPTY_PASSENGER,
    type: passenger.type,
    ...(passenger.nationality ? { nationality: passenger.nationality } : {}),
    ...(passenger.roomIndex === undefined ? {} : { roomIndex: passenger.roomIndex }),
    ...(index === 0 ? { isContactPerson: true } : {}),
  }));
}

/** Campos de una fila: la señal capturada puede sumar birthDate al contrato. */
export function bookingPartyRowFields(
  fields: readonly DeclaredPassengerField[],
  passenger: CommittedPassenger | undefined,
): DeclaredPassengerField[] {
  if (
    !compositionRequiresBirthDate(passenger) ||
    fields.some((entry) => entry.field === 'birthDate')
  ) {
    return [...fields];
  }
  return [...fields, { field: 'birthDate', level: 'hold' }];
}

export function bookingPartyBirthDateMissing(
  passenger: CommittedPassenger | undefined,
  value: RuteroPassengerPayload | undefined,
): boolean {
  return (
    compositionRequiresBirthDate(passenger) && !value?.birthDate?.trim()
  );
}

export function cleanPassengersForQuoteAndHold(
  passengers: readonly RuteroPassengerPayload[],
): RuteroPassengerPayload[] {
  return passengers.map((passenger) => {
    // `travelerId` pertenece al contrato interno UI → Bandido: el backend lo
    // usa para crear el vínculo histórico y `providerPassengers` lo elimina
    // antes del contrato externo Bandido → Rutero.
    const cleaned: RuteroPassengerPayload = {
      ...passenger,
      firstName: passenger.firstName.trim(),
      lastName: passenger.lastName.trim(),
    };
    delete cleaned.travelerSourceId;
    delete cleaned.identityDocument;
    for (const field of RUTERO_DECLARABLE_PASSENGER_FIELDS) {
      if (field === 'type') continue;
      if (!cleaned[field]?.trim()) delete cleaned[field];
    }
    return cleaned;
  });
}

export function bookingPartyValuesValid(
  party: readonly CommittedPassenger[],
  passengers: readonly RuteroPassengerPayload[],
): boolean {
  return passengers.every(
    (passenger, index) =>
      !bookingPartyBirthDateMissing(party[index], passenger),
  );
}

export function isBookingPartyFieldMissing(
  field: RuteroDeclarablePassengerField,
  partyPassenger: CommittedPassenger | undefined,
  passenger: RuteroPassengerPayload | undefined,
): boolean {
  return (
    field === 'birthDate' &&
    bookingPartyBirthDateMissing(partyPassenger, passenger)
  );
}
